import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { EuStars } from "../components/EuStars";
import SiteFooter from "../components/SiteFooter";
import { SeatStepper } from "./Onboarding";
import { useLang } from "../lib/i18n";

export default function Pricing() {
    const { user, refresh } = useAuth();
    const { T, lang } = useLang();
    const navigate = useNavigate();
    const location = useLocation();
    const [params] = useSearchParams();
    const isPublic = !location.pathname.startsWith("/app");
    const [catalogue, setCatalogue] = useState(null);
    const [cycle, setCycle] = useState(() =>
        params.get("cycle") === "annual" ? "annual" : "monthly",
    );
    const [checkoutBusy, setCheckoutBusy] = useState(null);
    const [teamSeats, setTeamSeats] = useState(() => {
        const s = parseInt(params.get("seats") || "3", 10);
        return Number.isFinite(s) && s >= 3 ? Math.min(s, 25) : 3;
    });
    const autoTriggeredRef = useRef(false);

    useEffect(() => {
        api.get("/billing/plans")
            .then(({ data }) => setCatalogue(data))
            .catch(() => toast.error("Could not load pricing"));
    }, []);

    const plans = useMemo(() => catalogue?.items ?? [], [catalogue]);

    async function startCheckout(planKey) {
        const seats = planKey === "team" ? teamSeats : 1;
        if (!user) {
            navigate(`/signup?plan=${planKey}&cycle=${cycle}&seats=${seats}`);
            return;
        }
        try {
            setCheckoutBusy(planKey);
            const { data } = await api.post("/billing/checkout", {
                plan_key: planKey,
                cycle,
                seats,
            });
            if (data.external && /^https?:/.test(data.hosted_url || "")) {
                window.location.href = data.hosted_url;
                return;
            }
            navigate(`/app/billing/checkout/${data.id}`);
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not start checkout";
            toast.error(msg);
            setCheckoutBusy(null);
        } finally {
            if (user) refresh();
        }
    }

    // Resume checkout after post-login redirect (query: ?plan=professional&cycle=monthly)
    useEffect(() => {
        if (autoTriggeredRef.current) return;
        const wanted = params.get("plan");
        if (!wanted || !user || !plans.length) return;
        if (!plans.some((p) => p.key === wanted)) return;
        if (user.plan_key === wanted) return;
        autoTriggeredRef.current = true;
        startCheckout(wanted);
    }, [user, plans, params]);

    const inner = (
        <div className="container-edit py-10 lg:py-16" data-testid="pricing-page">
            <div className="label-eyebrow">{T("Pricing", "Fiyatlandırma")}</div>
            <h1 className="font-display text-3xl lg:text-5xl mt-2 tracking-tight max-w-2xl">
                {T("Plans calibrated for legal practice.", "Hukuk pratiğine göre ölçülmüş planlar.")}
            </h1>
            <p className="mt-4 max-w-xl text-[#0D1B3E]/75">
                {T("Predictable monthly credits. Heavy features cost more credits per call, so you only pay for the depth you actually use.",
                    "Öngörülebilir aylık krediler. Ağır özellikler çağrı başına daha fazla kredi kullanır; yalnızca gerçekten kullandığınız derinlik için ödersiniz.")}
            </p>
            <p className="mt-3 text-sm text-[#C9A84C] uppercase tracking-[0.18em]" data-testid="pricing-trial-note">
                {T("New here? Every account starts with a 7-day free trial, no credit card.", "Yeni misiniz? Her hesap, kredi kartı gerekmeden 7 günlük ücretsiz denemeyle başlar.")}
            </p>
            <p className="mt-2 text-xs text-[#0D1B3E]/60">
                {T("Questions about VAT, invoices or refunds? See the", "KDV, fatura veya iadelerle ilgili sorular için bkz.")}{" "}
                <Link to="/refunds" className="underline hover:text-[#C9A84C]" data-testid="pricing-refund-link">{T("Refund Policy", "İade Politikası")}</Link>.
            </p>
            {lang === "tr" && (
                <p className="mt-2 text-xs text-[#0D1B3E]/60" data-testid="pricing-tr-currency-note">
                    Fiyatlar euro (EUR) cinsinden gösterilir; KDV ödeme adımında hesaplanır.
                </p>
            )}

            <CycleToggle cycle={cycle} onChange={setCycle} />

            {!catalogue ? (
                <p className="mt-10 text-[#0D1B3E]/60">{T("Loading plans…", "Planlar yükleniyor…")}</p>
            ) : (
                <div className="mt-8 grid gap-6 md:grid-cols-2 max-w-4xl" data-testid="pricing-grid">
                    {plans.map((p) => (
                        <PlanCard
                            key={p.key}
                            plan={p}
                            cycle={cycle}
                            active={user?.plan_key === p.key}
                            busy={checkoutBusy === p.key}
                            onSelect={() => startCheckout(p.key)}
                            teamSeats={teamSeats}
                            setTeamSeats={setTeamSeats}
                        />
                    ))}
                </div>
            )}

            <ComparisonTable plans={plans} />

            <Faq />

            <p className="mt-10 text-xs uppercase tracking-[0.22em] text-[#0D1B3E]/55">
                {T("Heavy features (Argument Generator, Comparative Law, Deep Research) consume multiple credits per call.",
                    "Ağır özellikler (Argüman Oluşturucu, Karşılaştırmalı Hukuk, Derin Araştırma) çağrı başına birden fazla kredi kullanır.")}
            </p>
        </div>
    );

    if (!isPublic) return inner;

    return (
        <div className="min-h-screen flex flex-col bg-[#F5F0E8] text-[#0D1B3E]">
            <header className="w-full">
                <div className="container-edit flex items-center justify-between py-6">
                    <Link to="/" data-testid="pricing-brand" className="flex items-center gap-3">
                        <EuStars size={32} />
                        <span className="font-display text-xl tracking-tight">EuroLaw Hub</span>
                    </Link>
                    <nav className="flex items-center gap-2 sm:gap-5">
                        {user ? (
                            <Link
                                to="/app"
                                data-testid="pricing-nav-workspace"
                                className="text-sm tracking-wide hover:text-[#C9A84C]"
                            >
                                {T("Open workspace", "Çalışma alanını aç")}
                            </Link>
                        ) : (
                            <Link
                                to="/login"
                                data-testid="pricing-nav-login"
                                className="text-sm tracking-wide hover:text-[#C9A84C]"
                            >
                                {T("Sign in", "Giriş yap")}
                            </Link>
                        )}
                        <Link
                            to={user ? "/app" : "/signup"}
                            data-testid="pricing-nav-cta"
                            className="btn-primary"
                        >
                            {user ? T("Open Workspace", "Çalışma Alanını Aç") : T("Start Free Trial", "Ücretsiz Deneme")} <ArrowRight size={16} />
                        </Link>
                    </nav>
                </div>
            </header>
            <main className="flex-1">{inner}</main>
            <SiteFooter />
        </div>
    );
}

function CycleToggle({ cycle, onChange }) {
    const { T } = useLang();
    return (
        <div
            className="mt-10 inline-flex items-center gap-1 border border-[#0D1B3E]/15 bg-white/70 p-1"
            data-testid="pricing-cycle-toggle"
        >
            {[
                { key: "monthly", label: T("Monthly", "Aylık") },
                { key: "annual", label: T("Annual · save ~20%", "Yıllık · ~%20 tasarruf") },
            ].map((opt) => {
                const on = cycle === opt.key;
                return (
                    <button
                        key={opt.key}
                        type="button"
                        data-testid={`pricing-cycle-${opt.key}`}
                        onClick={() => onChange(opt.key)}
                        className={`px-5 py-2 text-xs uppercase tracking-[0.22em] transition-colors ${
                            on
                                ? "bg-[#0D1B3E] text-[#F5F0E8]"
                                : "text-[#0D1B3E]/70 hover:text-[#0D1B3E]"
                        }`}
                    >
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}

function PlanCard({ plan, cycle, active, busy, onSelect, teamSeats, setTeamSeats }) {
    const { T } = useLang();
    const featured = plan.key === "professional";
    const isAnnual = cycle === "annual";
    const isTeam = plan.key === "team";
    const extraSeats = isTeam ? Math.max(0, teamSeats - (plan.included_seats || 3)) : 0;
    const monthlyTotal = plan.price_eur + extraSeats * (plan.extra_seat_eur || 0);
    const annualTotal = plan.annual_price_eur + extraSeats * (plan.extra_seat_annual_eur || 0);
    const displayPrice = isAnnual ? Math.round(annualTotal / 12) : monthlyTotal;
    const billingBlurb = isAnnual
        ? T(`Billed €${annualTotal.toLocaleString()} yearly`, `Yıllık €${annualTotal.toLocaleString()} olarak faturalandırılır`)
        : T("Billed monthly", "Aylık faturalandırılır");
    const pooledCredits = isTeam
        ? plan.monthly_queries + extraSeats * (plan.extra_seat_credits || 0)
        : plan.monthly_queries;
    return (
        <div
            className={`card-flat p-8 flex flex-col ${
                featured ? "border-[#C9A84C] bg-[#FFFDF7]" : ""
            }`}
            data-testid={`pricing-card-${plan.key}`}
        >
            {featured && <div className="label-eyebrow mb-3">{T("Most popular", "En popüler")}</div>}
            <div className="font-display text-2xl tracking-tight">{plan.name}</div>
            <div className="mt-3 flex items-baseline gap-2">
                <span
                    className="font-display text-4xl tracking-tight"
                    data-testid={`pricing-price-${plan.key}`}
                >
                    €{displayPrice}
                </span>
                <span className="text-xs uppercase tracking-[0.22em] text-[#0D1B3E]/55">
                    {T("/ month", "/ ay")}
                </span>
            </div>
            <p className="mt-1 text-xs uppercase tracking-[0.22em] text-[#0D1B3E]/55">
                {billingBlurb}
            </p>
            <p className="mt-3 text-sm text-[#0D1B3E]/75">{plan.description}</p>

            {isTeam && (
                <SeatStepper
                    seats={teamSeats}
                    setSeats={setTeamSeats}
                    min={plan.included_seats || 3}
                    max={plan.max_seats || 25}
                    extraEur={plan.extra_seat_eur || 49}
                />
            )}

            <hr className="gold-rule my-6" />

            <ul className="space-y-2.5 text-sm">
                <Feature>
                    {pooledCredits.toLocaleString()} {T("monthly credits", "aylık kredi")}
                    {isTeam ? T(" (pooled across the team)", " (ekip içinde ortak havuz)") : ""}
                </Feature>
                <Feature>
                    {plan.allow_uploads
                        ? T(`Up to ${plan.max_uploads} document uploads / mo`, `Ayda ${plan.max_uploads} belge yüklemeye kadar`)
                        : T("No document uploads", "Belge yükleme yok")}
                </Feature>
                <Feature>
                    {plan.heavy_features
                        ? T("Argument Generator + Comparative Law", "Argüman Oluşturucu + Karşılaştırmalı Hukuk")
                        : T("Standard research modules", "Standart araştırma modülleri")}
                </Feature>
                <Feature>
                    {plan.key === "team"
                        ? T(`${plan.included_seats || 3} seats included · invite members with roles`, `${plan.included_seats || 3} kullanıcı dahil · rollerle üye davet edin`)
                        : T("Personal watch alerts", "Kişisel takip uyarıları")}
                </Feature>
                <Feature>
                    {plan.key === "team" ? T("Priority support & SSO on request", "Öncelikli destek ve talep üzerine SSO") : T("Email support", "E-posta desteği")}
                </Feature>
            </ul>

            <div className="mt-8">
                {active ? (
                    <button
                        type="button"
                        disabled
                        className="btn-secondary w-full opacity-60 cursor-not-allowed"
                        data-testid={`pricing-current-${plan.key}`}
                    >
                        {T("Current plan", "Mevcut plan")}
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={onSelect}
                        disabled={busy}
                        className={featured ? "btn-primary w-full" : "btn-secondary w-full"}
                        data-testid={`pricing-cta-${plan.key}`}
                    >
                        {busy ? (
                            <>
                                <Loader2 size={16} className="animate-spin" /> {T("Preparing checkout…", "Ödeme hazırlanıyor…")}
                            </>
                        ) : (
                            <>{T("Choose", "Seç:")} {plan.name}{isTeam ? ` · ${teamSeats} ${T("seats", "kullanıcı")}` : ""}</>
                        )}
                    </button>
                )}
            </div>
        </div>
    );
}

function Feature({ children }) {
    return (
        <li className="flex items-start gap-2.5">
            <Check size={16} color="#C9A84C" className="mt-0.5 shrink-0" />
            <span className="text-[#0D1B3E]/85">{children}</span>
        </li>
    );
}

function ComparisonTable({ plans }) {
    const { T } = useLang();
    if (!plans.length) return null;
    const rows = [
        { label: T("Monthly credits", "Aylık krediler"), get: (p) => p.monthly_queries.toLocaleString() },
        {
            label: T("Document uploads / month", "Aylık belge yükleme"),
            get: (p) => (p.allow_uploads ? p.max_uploads : "–"),
        },
        {
            label: T("Argument Generator + Comparative Law", "Argüman Oluşturucu + Karşılaştırmalı Hukuk"),
            get: (p) => (p.heavy_features ? T("Included", "Dahil") : "–"),
        },
        {
            label: T("Watchdogs & Alerts", "Takipçiler ve Uyarılar"),
            get: (p) => (p.key === "team" ? T("Multi-watch", "Çoklu takip") : T("Personal", "Kişisel")),
        },
        {
            label: T("Bulletin generation", "Bülten oluşturma"),
            get: () => T("Included", "Dahil"),
        },
    ];
    return (
        <div className="mt-16 max-w-4xl" data-testid="pricing-comparison">
            <div className="label-eyebrow">{T("Compare", "Karşılaştırın")}</div>
            <h2 className="font-display text-2xl mt-1 tracking-tight">{T("What\u2019s inside each plan.", "Her planın içinde ne var.")}</h2>
            <div className="mt-6 border border-[#0D1B3E]/12 bg-white/70">
                <div
                    className="grid text-sm"
                    style={{ gridTemplateColumns: `1.4fr repeat(${plans.length}, 1fr)` }}
                >
                    <div className="px-5 py-3 text-[11px] uppercase tracking-[0.22em] text-[#0D1B3E]/55 border-b border-[#0D1B3E]/10">
                        {T("Feature", "Özellik")}
                    </div>
                    {plans.map((p) => (
                        <div
                            key={p.key}
                            className="px-5 py-3 text-[11px] uppercase tracking-[0.22em] text-[#0D1B3E]/55 border-b border-[#0D1B3E]/10"
                        >
                            {p.name}
                        </div>
                    ))}
                    {rows.map((row) => (
                        <React.Fragment key={row.label}>
                            <div className="px-5 py-3 border-b border-[#0D1B3E]/10 text-[#0D1B3E]/80">
                                {row.label}
                            </div>
                            {plans.map((p) => (
                                <div
                                    key={p.key + row.label}
                                    className="px-5 py-3 border-b border-[#0D1B3E]/10"
                                >
                                    {row.get(p)}
                                </div>
                            ))}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </div>
    );
}

const FAQ_ITEMS = [
    {
        q: ["How does the 7-day free trial work?", "7 günlük ücretsiz deneme nasıl çalışır?"],
        a: ["Every new account starts with full platform access for 7 days and 15 credits. No credit card is required, and the trial ends automatically; nothing is charged unless you choose a plan.",
            "Her yeni hesap, 7 gün boyunca tam platform erişimi ve 15 krediyle başlar. Kredi kartı gerekmez ve deneme otomatik olarak sona erer; bir plan seçmedikçe ücret alınmaz."],
    },
    {
        q: ["What is a credit?", "Kredi nedir?"],
        a: ["One credit covers a standard research question. Heavier work such as comparative analyses, the Argument Generator or deep research consumes more credits per call. Unused credits reset monthly.",
            "Bir kredi, standart bir araştırma sorusunu karşılar. Karşılaştırmalı analizler, Argüman Oluşturucu veya derin araştırma gibi ağır işlemler çağrı başına daha fazla kredi kullanır. Kullanılmayan krediler her ay sıfırlanır."],
    },
    {
        q: ["Does EuroLaw Hub cover Turkish law?", "EuroLaw Hub Türk hukukunu kapsıyor mu?"],
        a: ["Yes. On the Professional and Team plans you can switch the jurisdiction to Türkiye or run EU and Türkiye comparisons. Turkish answers are verified against official sources such as mevzuat.gov.tr and the Resmi Gazete.",
            "Evet. Profesyonel ve Ekip planlarında yargı alanını Türkiye olarak seçebilir veya AB ile Türkiye karşılaştırmaları yapabilirsiniz. Türk hukuku yanıtları mevzuat.gov.tr ve Resmî Gazete gibi resmi kaynaklarda doğrulanır."],
    },
    {
        q: ["Which currency do you charge in, and what about VAT?", "Hangi para biriminde ücretlendirme yapılıyor, KDV ne olacak?"],
        a: ["All prices are in euros (EUR). Applicable VAT is calculated at checkout based on your billing details. Invoices are available from the billing portal.",
            "Tüm fiyatlar euro (EUR) cinsindendir. Geçerli KDV, fatura bilgilerinize göre ödeme adımında hesaplanır. Faturalara ödeme portalından erişilebilir."],
    },
    {
        q: ["Can I cancel at any time?", "İstediğim zaman iptal edebilir miyim?"],
        a: ["Yes. You can cancel from the billing portal; access continues to the end of the paid period and your saved work is preserved. See the Refund Policy for details.",
            "Evet. Ödeme portalından iptal edebilirsiniz; erişiminiz ödenen dönemin sonuna kadar sürer ve kayıtlı çalışmalarınız korunur. Ayrıntılar için İade Politikası'na bakın."],
    },
];

function Faq() {
    const { lang, T } = useLang();
    const i = lang === "tr" ? 1 : 0;
    return (
        <div className="mt-16 max-w-3xl" data-testid="pricing-faq">
            <div className="label-eyebrow">{T("FAQ", "SSS")}</div>
            <h2 className="font-display text-2xl mt-1 tracking-tight">
                {T("Frequently asked questions.", "Sıkça sorulan sorular.")}
            </h2>
            <div className="mt-6 divide-y divide-[#0D1B3E]/10 border border-[#0D1B3E]/12 bg-white/70">
                {FAQ_ITEMS.map((item, idx) => (
                    <details key={idx} className="group px-5 py-4" data-testid={`pricing-faq-item-${idx}`}>
                        <summary className="cursor-pointer list-none font-medium text-[15px] text-[#0D1B3E] hover:text-[#C9A84C] flex items-center justify-between gap-4 min-h-[44px]">
                            {item.q[i]}
                            <span className="text-[#C9A84C] group-open:rotate-45 transition-transform text-lg leading-none">+</span>
                        </summary>
                        <p className="mt-2 pb-1 text-sm leading-relaxed text-[#0D1B3E]/75">{item.a[i]}</p>
                    </details>
                ))}
            </div>
        </div>
    );
}
